/**
 * packages/server/src/database.ts
 *
 * DB Connection Factory — PGlite / PostgreSQL 환경변수 분기.
 *
 * DATABASE_URL 없음 → PGlite (임베디드, ./data)
 * DATABASE_URL 있음 → PostgreSQL (실제 서버 연결)
 *
 * Drizzle ORM 인스턴스는 동일 API → 사용자 함수 코드 변경 없음.
 */

import { createHash } from "crypto";
import { existsSync, mkdirSync, mkdtempSync, readdirSync, readFileSync, rmSync, writeFileSync } from "fs";
import { tmpdir } from "os";
import { join, resolve } from "path";
import { resolvePostgresPoolConfig } from "./database-config.js";

// ─── 공통 인터페이스 ────────────────────────────────────────

export interface DbConnection {
  /** Drizzle ORM 인스턴스 */
  db: any;
  /** System-plane Drizzle ORM 인스턴스 used for ctx.unsafeDb */
  systemDb: any;
  /** Raw SQL 실행 (admin API용) — 항상 rows 배열 반환 */
  rawSql: RawSqlExecutor;
  /** Platform 내부 작업용 Raw SQL — RLS system GUC를 같은 transaction에 주입 */
  systemRawSql: RawSqlExecutor;
  /** 마이그레이션 적용 */
  migrate: (migrationsFolder: string) => Promise<void>;
  /** 연결 종료 */
  close: () => Promise<void>;
  /** 현재 드라이버 */
  driver: "pglite" | "postgres";
}

export type RawSqlExecutor = ((sql: string, params?: any[]) => Promise<any[]>) & {
  transaction?: <T>(callback: (rawSql: RawSqlExecutor) => Promise<T>) => Promise<T>;
};

export function resolveSystemDatabaseUrl(
  databaseUrl: string,
  env: Record<string, string | undefined> = process.env,
): string | null {
  // Platform control-plane runtime should keep using the platform DB connection
  // plus system-scoped GUC injection (systemRawSql / createPlatformSystemDb),
  // not the tenant-app gencow_system_user role. If we derive a system-role URL
  // while IS_PLATFORM=true, platform workers start querying gencow_platform as
  // gencow_system_user and lose access to control-plane tables such as
  // platform_runtime_state and scheduled_jobs.
  if (env.IS_PLATFORM === "true") return null;

  const explicit = env.GENCOW_SYSTEM_DATABASE_URL?.trim();
  if (explicit) return explicit;

  const systemPassword = env.GENCOW_SYSTEM_USER_PASS?.trim();
  if (!systemPassword) return null;

  const url = new URL(databaseUrl);
  url.username = "gencow_system_user";
  url.password = systemPassword;
  url.searchParams.delete("options");
  return url.toString();
}

interface PreparedRuntimeMigrationsFolder {
  path: string;
  cleanup: () => void;
}

interface LegacyJournalEntry {
  idx?: number;
  tag?: string;
  when?: number;
}

export interface RuntimeMigrationDescriptor {
  name: string;
  folderMillis: number;
  hash: string;
}

export interface LegacyDrizzleMigrationRow {
  id: number;
  hash: string;
  createdAt: number;
}

export interface LegacyDrizzleMigrationNameAssignment {
  id: number;
  name: string;
  reason: "matched" | "inferred";
}

export interface LegacyDrizzleMigrationTableRepairPlan {
  dbCount: number;
  localCount: number;
  matchedCount: number;
  inferredCount: number;
  assignments: LegacyDrizzleMigrationNameAssignment[];
}

function formatMigrationTimestamp(millis: number): string {
  const d = new Date(Math.floor(millis / 1000) * 1000);
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return [
    pad(d.getUTCFullYear(), 4),
    pad(d.getUTCMonth() + 1),
    pad(d.getUTCDate()),
    pad(d.getUTCHours()),
    pad(d.getUTCMinutes()),
    pad(d.getUTCSeconds()),
  ].join("");
}

function safeMigrationTag(tag: string): string {
  return tag.replace(/[^a-zA-Z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "migration";
}

export function prepareRuntimeMigrationsFolder(migrationsFolder: string): PreparedRuntimeMigrationsFolder {
  const journalPath = join(migrationsFolder, "meta", "_journal.json");
  if (!existsSync(journalPath)) {
    return { path: migrationsFolder, cleanup: () => {} };
  }

  const journal = JSON.parse(readFileSync(journalPath, "utf-8")) as { entries?: LegacyJournalEntry[] };
  const entries = Array.isArray(journal.entries) ? journal.entries : [];
  const compatDir = mkdtempSync(join(tmpdir(), "gencow-runtime-migrations-"));
  let copied = 0;

  for (const [index, entry] of entries.entries()) {
    if (!entry.tag) continue;
    const sqlPath = join(migrationsFolder, `${entry.tag}.sql`);
    if (!existsSync(sqlPath)) continue;

    const when = Number.isFinite(entry.when) ? Number(entry.when) : Date.UTC(2000, 0, 1, 0, 0, index);
    const dirName = `${formatMigrationTimestamp(when)}_${String(entry.idx ?? index).padStart(4, "0")}_${safeMigrationTag(entry.tag)}`;
    const targetDir = join(compatDir, dirName);
    mkdirSync(targetDir, { recursive: true });
    writeFileSync(join(targetDir, "migration.sql"), readFileSync(sqlPath, "utf-8"));
    copied++;
  }

  if (copied === 0) {
    rmSync(compatDir, { recursive: true, force: true });
    return { path: migrationsFolder, cleanup: () => {} };
  }

  return {
    path: compatDir,
    cleanup: () => rmSync(compatDir, { recursive: true, force: true }),
  };
}

function parseMigrationDirTimestamp(dir: string): number | null {
  const match = dir.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  if (!match) return null;
  return Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +match[6]);
}

function roundMigrationMillis(value: number): number {
  return Math.floor(value / 1000) * 1000;
}

export function collectRuntimeMigrationDescriptors(migrationsFolder: string): RuntimeMigrationDescriptor[] {
  return readdirSync(migrationsFolder)
    .map((name) => {
      const migrationSqlPath = join(migrationsFolder, name, "migration.sql");
      const folderMillis = parseMigrationDirTimestamp(name);
      if (folderMillis === null || !existsSync(migrationSqlPath)) return null;
      const query = readFileSync(migrationSqlPath, "utf-8");
      return {
        name,
        folderMillis,
        hash: createHash("sha256").update(query).digest("hex"),
      };
    })
    .filter((migration): migration is RuntimeMigrationDescriptor => migration !== null)
    .sort((a, b) => a.folderMillis - b.folderMillis || a.name.localeCompare(b.name));
}

export function planLegacyDrizzleMigrationTableRepair(
  dbRows: LegacyDrizzleMigrationRow[],
  localMigrations: RuntimeMigrationDescriptor[],
): LegacyDrizzleMigrationTableRepairPlan {
  const byMillis = new Map<number, RuntimeMigrationDescriptor[]>();
  const byHash = new Map<string, RuntimeMigrationDescriptor>();
  for (const migration of localMigrations) {
    const existing = byMillis.get(migration.folderMillis) ?? [];
    existing.push(migration);
    byMillis.set(migration.folderMillis, existing);
    byHash.set(migration.hash, migration);
  }

  const assignedNames = new Set<string>();
  const assignments: LegacyDrizzleMigrationNameAssignment[] = [];
  const unmatchedRows: LegacyDrizzleMigrationRow[] = [];

  for (const row of dbRows) {
    const rowMillis = roundMigrationMillis(row.createdAt);
    const candidates = byMillis.get(rowMillis);
    let matched: RuntimeMigrationDescriptor | undefined;
    if (candidates?.length === 1) {
      matched = candidates[0];
    } else if (candidates && candidates.length > 1) {
      matched = candidates.find((candidate) => candidate.hash === row.hash);
    } else {
      matched = byHash.get(row.hash);
    }

    if (matched) {
      assignedNames.add(matched.name);
      assignments.push({ id: row.id, name: matched.name, reason: "matched" });
    } else {
      unmatchedRows.push(row);
    }
  }

  const maxUnmatchedMillis =
    unmatchedRows.length > 0
      ? Math.max(...unmatchedRows.map((row) => roundMigrationMillis(row.createdAt)))
      : -Infinity;
  const inferredBackfill = localMigrations.filter(
    (migration) => !assignedNames.has(migration.name) && migration.folderMillis <= maxUnmatchedMillis,
  );

  for (const [index, row] of unmatchedRows.entries()) {
    const migration = inferredBackfill[index];
    if (!migration) break;
    assignedNames.add(migration.name);
    assignments.push({ id: row.id, name: migration.name, reason: "inferred" });
  }

  return {
    dbCount: dbRows.length,
    localCount: localMigrations.length,
    matchedCount: assignments.filter((assignment) => assignment.reason === "matched").length,
    inferredCount: assignments.filter((assignment) => assignment.reason === "inferred").length,
    assignments,
  };
}

function toLegacyDrizzleMigrationRow(row: Record<string, unknown>): LegacyDrizzleMigrationRow | null {
  const id = Number(row.id);
  const hash = typeof row.hash === "string" ? row.hash : String(row.hash ?? "");
  const createdAt = Number(row.createdAt ?? row.created_at);
  if (!Number.isFinite(id) || !hash || !Number.isFinite(createdAt)) return null;
  return { id, hash, createdAt };
}

async function repairLegacyDrizzleMigrationTableForPlatformRuntime(
  rawSql: RawSqlExecutor,
  migrationsFolder: string,
): Promise<LegacyDrizzleMigrationTableRepairPlan | null> {
  if (process.env.IS_PLATFORM !== "true") return null;

  const tableRows = await rawSql(
    `SELECT 1 FROM information_schema.tables WHERE table_schema = $1 AND table_name = $2`,
    ["drizzle", "__drizzle_migrations"],
  );
  if (tableRows.length === 0) return null;

  const columnRows = await rawSql(
    `SELECT column_name FROM information_schema.columns WHERE table_schema = $1 AND table_name = $2`,
    ["drizzle", "__drizzle_migrations"],
  );
  const columns = new Set(columnRows.map((row) => String((row as Record<string, unknown>).column_name)));
  if (columns.has("name") && columns.has("applied_at")) return null;

  const localMigrations = collectRuntimeMigrationDescriptors(migrationsFolder);
  if (localMigrations.length === 0) return null;

  const dbRows = (
    await rawSql(
      `SELECT id, hash, created_at AS "createdAt" FROM "drizzle"."__drizzle_migrations" ORDER BY id ASC`,
    )
  )
    .map((row) => toLegacyDrizzleMigrationRow(row as Record<string, unknown>))
    .filter((row): row is LegacyDrizzleMigrationRow => row !== null);
  if (dbRows.length === 0) return null;

  const plan = planLegacyDrizzleMigrationTableRepair(dbRows, localMigrations);
  if (plan.assignments.length === 0) return null;

  const executeRepair = async (execute: RawSqlExecutor) => {
    await execute(`ALTER TABLE "drizzle"."__drizzle_migrations" ADD COLUMN IF NOT EXISTS "name" text`);
    await execute(
      `ALTER TABLE "drizzle"."__drizzle_migrations" ADD COLUMN IF NOT EXISTS "applied_at" timestamp with time zone DEFAULT now()`,
    );
    for (const assignment of plan.assignments) {
      await execute(
        `UPDATE "drizzle"."__drizzle_migrations" SET "name" = $1, "applied_at" = NULL WHERE "id" = $2`,
        [assignment.name, assignment.id],
      );
    }
  };

  if (rawSql.transaction) {
    await rawSql.transaction(executeRepair);
  } else {
    await executeRepair(rawSql);
  }

  return plan;
}

// ─── Factory ────────────────────────────────────────────────

export async function createDatabase(): Promise<DbConnection> {
  const databaseUrl = process.env.DATABASE_URL;

  if (databaseUrl) {
    return initPostgres(databaseUrl);
  }

  const dataDir = process.env.GENCOW_DB_URL || "./data";
  return initPGlite(dataDir);
}

// ─── PGlite ─────────────────────────────────────────────────

async function initPGlite(dataDir: string): Promise<DbConnection> {
  const { PGlite } = await import("@electric-sql/pglite");
  const { drizzle } = await import("drizzle-orm/pglite");

  // Remove stale postmaster.pid left behind by kill -9.
  try {
    const { unlinkSync } = await import("fs");
    unlinkSync(resolve(dataDir, "postmaster.pid"));
    console.log("[db] removed stale postmaster.pid");
  } catch {
    /* file didn't exist — normal */
  }

  // pgvector 확장 — RAG/Memory의 vector 타입 사용을 위해 항상 로딩
  let vectorExt: Record<string, unknown> = {};
  try {
    const { vector } = await import("@electric-sql/pglite/vector");
    vectorExt = { vector };
  } catch {
    /* vector 확장이 없어도 기본 기능은 동작 */
  }

  const client = new PGlite({ dataDir, extensions: vectorExt } as any);
  const db = (drizzle as any)({ client });

  console.log(`[db] PGlite mode — ${dataDir}`);

  async function setPlatformSystemGucs(executor: {
    query: (sql: string, params?: any[]) => Promise<unknown>;
  }) {
    await executor.query("SELECT set_config($1::text, $2::text, true)", ["app.current_user_id", ""]);
    await executor.query("SELECT set_config($1::text, $2::text, true)", ["app.current_user_role", "system"]);
    await executor.query("SELECT set_config($1::text, $2::text, true)", ["app.internal_system", "true"]);
  }

  const rawSql = (async (sql: string, params?: any[]): Promise<any[]> => {
    if (params && params.length > 0) {
      const result = await client.query(sql, params);
      return result.rows;
    }
    const result = await client.exec(sql);
    return result[0]?.rows || [];
  }) as RawSqlExecutor;
  rawSql.transaction = async (callback) =>
    (client as any).transaction(async (tx: any) => {
      const txRawSql = (async (sql: string, params?: any[]): Promise<any[]> => {
        const result = params && params.length > 0 ? await tx.query(sql, params) : await tx.query(sql);
        return result.rows || [];
      }) as RawSqlExecutor;
      return callback(txRawSql);
    });

  const systemRawSql = (async (sql: string, params?: any[]): Promise<any[]> => {
    return await (client as any).transaction(async (tx: any) => {
      await setPlatformSystemGucs(tx);
      if (params && params.length > 0) {
        const result = await tx.query(sql, params);
        return result.rows;
      }
      const result = await tx.query(sql);
      return result.rows || [];
    });
  }) as RawSqlExecutor;
  systemRawSql.transaction = async (callback) =>
    (client as any).transaction(async (tx: any) => {
      await setPlatformSystemGucs(tx);
      const txRawSql = (async (sql: string, params?: any[]): Promise<any[]> => {
        const result = params && params.length > 0 ? await tx.query(sql, params) : await tx.query(sql);
        return result.rows || [];
      }) as RawSqlExecutor;
      return callback(txRawSql);
    });

  return {
    db,
    systemDb: db,
    driver: "pglite",
    rawSql,
    systemRawSql,

    async migrate(migrationsFolder: string): Promise<void> {
      // vector 확장이 로드되었으면 마이그레이션 전에 활성화
      if (Object.keys(vectorExt).length > 0) {
        await client.exec("CREATE EXTENSION IF NOT EXISTS vector;");
      }
      const { migrate } = await import("drizzle-orm/pglite/migrator");
      const prepared = prepareRuntimeMigrationsFolder(migrationsFolder);
      try {
        await migrate(db, { migrationsFolder: prepared.path });
      } finally {
        prepared.cleanup();
      }
    },

    async close(): Promise<void> {
      try {
        await client.close();
      } catch {}
    },
  };
}

// ─── PostgreSQL ─────────────────────────────────────────────

async function initPostgres(url: string): Promise<DbConnection> {
  const { SQL } = await import("bun");
  const { drizzle } = await import("drizzle-orm/bun-sql");

  // Database-per-Tenant: 각 앱이 전용 DB에 직접 연결하므로 search_path 불필요.
  // PgBouncer(6432)를 경유하여 커넥션 풀링 적용.

  // 커넥션 풀 크기/타임아웃은 앱 workload별로 provisioner가 주입할 수 있다.
  const poolConfig = resolvePostgresPoolConfig();
  const client = new SQL({
    url,
    ...poolConfig,
  });
  const db = drizzle({ client });
  // drizzle-orm/bun-sql enables tagged-template SQL by default (db.tagged=true).
  // In rc.3 that path builds empty SELECT/WHERE chunks → `select from "user"` syntax errors.
  // Force the unsafe(query.sql, params) executor until upstream fixes tagged mode.
  // @see docs/notes/note-drizzle-bun-sql-tagged-mode.md
  (db as { tagged: boolean }).tagged = false;

  const systemUrl = resolveSystemDatabaseUrl(url);
  const systemClient =
    systemUrl && systemUrl !== url
      ? new SQL({
          url: systemUrl,
          ...poolConfig,
        })
      : client;
  const systemDb = systemClient === client ? db : drizzle({ client: systemClient });
  (systemDb as { tagged: boolean }).tagged = false;

  // Mask password in log output
  const safeUrl = url.replace(/:([^@]+)@/, ":***@");
  const safeSystemUrl = systemUrl?.replace(/:([^@]+)@/, ":***@") ?? null;
  // Database-per-Tenant: URL에서 dbname을 추출하여 표시
  let dbNameInfo = "";
  try {
    const parsed = new URL(url);
    const dbName = parsed.pathname.replace("/", "");
    if (dbName && dbName !== "gencow_platform") {
      dbNameInfo = ` (db: ${dbName})`;
    }
  } catch {
    /* URL 파싱 실패 시 무시 */
  }
  console.log(`[db] Bun.sql mode — ${safeUrl}${dbNameInfo}`);
  if (safeSystemUrl) {
    console.log(`[db] Bun.sql system mode — ${safeSystemUrl}${dbNameInfo}`);
  } else {
    console.warn(
      "[db] GENCOW_SYSTEM_DATABASE_URL/GENCOW_SYSTEM_USER_PASS not configured — ctx.unsafeDb falls back to the tenant DB handle",
    );
  }

  async function setPlatformSystemGucs(executor: {
    unsafe: (sql: string, params?: any[]) => Promise<unknown>;
  }) {
    await executor.unsafe("SELECT set_config($1::text, $2::text, true)", ["app.current_user_id", ""]);
    await executor.unsafe("SELECT set_config($1::text, $2::text, true)", ["app.current_user_role", "system"]);
    await executor.unsafe("SELECT set_config($1::text, $2::text, true)", ["app.internal_system", "true"]);
  }

  const rawSql = (async (query: string, params?: unknown[]): Promise<unknown[]> => {
    if (params && params.length > 0) {
      const result = await client.unsafe(query, params as any[]);
      return Array.from(result);
    }
    const result = await client.unsafe(query);
    return Array.from(result);
  }) as RawSqlExecutor;
  rawSql.transaction = async (callback) =>
    (client as any).begin(async (tx: any) => {
      const txRawSql = (async (query: string, params?: unknown[]): Promise<unknown[]> => {
        const result =
          params && params.length > 0 ? await tx.unsafe(query, params as any[]) : await tx.unsafe(query);
        return Array.from(result);
      }) as RawSqlExecutor;
      return callback(txRawSql);
    });

  const systemRawSql = (async (query: string, params?: unknown[]): Promise<unknown[]> => {
    return await (systemClient as any).begin(async (tx: any) => {
      await setPlatformSystemGucs(tx);
      const result =
        params && params.length > 0 ? await tx.unsafe(query, params as any[]) : await tx.unsafe(query);
      return Array.from(result);
    });
  }) as RawSqlExecutor;
  systemRawSql.transaction = async (callback) =>
    (systemClient as any).begin(async (tx: any) => {
      await setPlatformSystemGucs(tx);
      const txRawSql = (async (query: string, params?: unknown[]): Promise<unknown[]> => {
        const result =
          params && params.length > 0 ? await tx.unsafe(query, params as any[]) : await tx.unsafe(query);
        return Array.from(result);
      }) as RawSqlExecutor;
      return callback(txRawSql);
    });

  return {
    db,
    systemDb,
    driver: "postgres",
    rawSql,
    systemRawSql,

    async migrate(migrationsFolder: string): Promise<void> {
      const { migrate } = await import("drizzle-orm/bun-sql/migrator");
      const prepared = prepareRuntimeMigrationsFolder(migrationsFolder);
      try {
        const repairPlan = await repairLegacyDrizzleMigrationTableForPlatformRuntime(rawSql, prepared.path);
        if (repairPlan) {
          console.warn(
            `[db] legacy drizzle migration table repaired for runtime compatibility ` +
              `(matched=${repairPlan.matchedCount}, inferred=${repairPlan.inferredCount}, ` +
              `dbRows=${repairPlan.dbCount}, local=${repairPlan.localCount})`,
          );
        }
        await migrate(db, { migrationsFolder: prepared.path });
      } finally {
        prepared.cleanup();
      }
    },

    async close(): Promise<void> {
      try {
        await client.close();
      } catch {}
      if (systemClient !== client) {
        try {
          await systemClient.close();
        } catch {}
      }
    },
  };
}
